import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva("relative rounded-lg border", {
  variants: {
    variant: {
      default: "border-border bg-background text-foreground",
      warning: "border-amber-500/50 text-amber-600 bg-amber-50 dark:bg-amber-950/20",
      error: "border-red-500/50 text-red-600 bg-red-50 dark:bg-red-950/20",
      success: "border-emerald-500/50 text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20",
      info: "border-blue-500/50 text-blue-600 bg-blue-50 dark:bg-blue-950/20",
      destructive:
        "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
    },
    size: {
      sm: "px-4 py-3",
      lg: "p-4",
    },
    isNotification: {
      true: "z-[100] max-w-[400px] bg-background shadow-lg shadow-black/5",
      false: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "sm",
    isNotification: false,
  },
})

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: React.ReactNode
  action?: React.ReactNode
  layout?: "row" | "complex"
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant,
      size,
      isNotification,
      icon,
      action,
      layout = "row",
      children,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant, size, isNotification }), className)}
      {...props}
    >
      {layout === "row" ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 grow">
            {icon && <span className="shrink-0">{icon}</span>}
            <div className="grow">{children}</div>
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {icon && children ? (
            <div className="flex gap-3">
              <span className="shrink-0">{icon}</span>
              <div className="grow">{children}</div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {icon && <span className="shrink-0">{icon}</span>}
              <div className="grow">{children}</div>
            </div>
          )}
          {action && <div className="flex justify-end">{action}</div>}
        </div>
      )}
    </div>
  ),
)
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

const AlertContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1", className)}
    {...props}
  />
))
AlertContent.displayName = "AlertContent"

export { Alert, AlertTitle, AlertDescription, AlertContent }
